import { styled } from '@linaria/react';

import { Section as BaseSection } from '../../components/Section';
import {
  bodyFont,
  fonts,
  layout,
  primitives,
  semantic,
  shadow,
  size,
  space,
} from '../../theme/tokens';

export const Section = styled(BaseSection)`
  && {
    padding-bottom: ${space['space-800']};
  }
`;

export const DetailImageSlot = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: ${space['space-600']};

  @media (max-width: ${layout.desktopBreakpoint}) {
    padding: 0;
  }
`;

export const DetailImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 800 / 450;
  border-radius: ${size.radius['radius-md']};
  overflow: hidden;

  @media (max-width: ${layout.desktopBreakpoint}) {
    border-radius: ${size.radius['radius-xl']} ${size.radius['radius-xl']} 0 0;
  }
`;

export const DetailTrack = styled.div<{ $slideIndex: number }>`
  display: flex;
  height: 100%;
  transform: translateX(${({ $slideIndex }) => `${$slideIndex * -100}%`});
  transition: transform 300ms ease;
`;

export const DetailSlide = styled.div`
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
`;

export const DetailSlideImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const DetailPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${semantic.bg.surfaceSubtle};
  color: ${semantic.text.tertiary};
  font-family: ${bodyFont};
  font-size: ${fonts.size.xs};
`;

export const DetailCarouselControls = styled.div`
  position: absolute;
  left: ${space['space-600']};
  right: ${space['space-600']};
  bottom: ${space['space-400']};
  height: 48px;
  pointer-events: none;
`;

export const DetailDots = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: ${space['space-200']};
`;

export const DetailDot = styled.button`
  width: 8px;
  height: 4px;
  padding: 0;
  border: none;
  border-radius: ${size.radius['radius-full']};
  background: ${semantic.bg.canvas};
  opacity: 0.5;
  cursor: pointer;
  pointer-events: auto;
  transition: width 200ms ease, opacity 200ms ease;

  &[data-active='true'] {
    width: 16px;
    opacity: 1;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.text.inverse};
    outline-offset: ${space['space-050']};
  }
`;

export const DetailNav = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  gap: ${space['space-100']};
  padding: ${space['space-100']};
  border-radius: ${size.radius['radius-full']};
  background: ${semantic.bg.canvas};
  box-shadow: ${shadow.sm};
  pointer-events: auto;
`;

export const DetailNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: ${size.radius['radius-full']};
  background: none;
  color: ${semantic.text.primary};
  cursor: pointer;

  &:hover {
    background: ${semantic.bg.surfaceSubtle};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const DetailClose = styled.button`
  position: absolute;
  top: ${space['space-600']};
  right: ${space['space-600']};
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${space['space-1200']};
  height: ${space['space-1200']};
  padding: 0;
  border: none;
  border-radius: ${size.radius['radius-full']};
  background: ${semantic.bg.canvas};
  color: ${semantic.icon.primary};
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${semantic.bg.surfaceSubtle};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-600']};
  padding: ${space['space-400']} ${space['space-600']};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space['space-400']} ${space['space-1200']};
  }
`;

export const DetailHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-100']};
`;

export const DetailTitle = styled.h2`
  margin: 0;
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.normal};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.primary};
`;

export const DetailLabel = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.secondary};
`;

export const DetailDescription = styled.p`
  margin: 0;
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.primary};
`;

export const DetailSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space['space-400']};
`;

export const DetailSectionHeading = styled.h3`
  margin: 0;
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.medium};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.primary};
`;

export const OpeningHoursRow = styled.div`
  display: flex;
  gap: ${space['space-100']};
  align-items: flex-start;
`;

export const OpeningHoursDay = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-100']};
`;

export const OpeningDayName = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.primary};
`;

export const OpeningDayTime = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.secondary};
`;

export const TodayBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${space['space-100']} ${space['space-300']};
  border-radius: ${size.radius['radius-full']};
  background: ${semantic.bg.infoSubtle};
  color: ${semantic.text.info};
  font-family: ${bodyFont};
  font-size: ${fonts.size.xs};
  font-weight: ${fonts.weight.medium};
  line-height: ${primitives.typography.lineHeight.normal};
  white-space: nowrap;
`;

export const DetailMenus = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space['space-200']};
`;

export const PillButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: ${space['space-300']} ${space['space-500']};
  border: none;
  border-radius: ${size.radius['radius-full']};
  background: ${semantic.bg.surfaceSubtle};
  color: ${semantic.text.primary};
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.medium};
  line-height: ${primitives.typography.lineHeight.normal};
  text-decoration: none;
  cursor: pointer;

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const DetailActionBar = styled.div`
  padding: ${space['space-600']} ${space['space-1000']};
`;
